import React from 'react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InvoiceStatus } from 'shared';

interface InvoiceToolbarProps {
  selectedYear: number | null;
  onYearChange: (year: number | null) => void;
  selectedStatus: InvoiceStatus | 'ALL';
  onStatusChange: (status: InvoiceStatus | 'ALL') => void;
  selectedInvoices: any[];
  onGenerateAll: () => void;
  onCreateCustom: () => void;
  onApproveSelected: () => void;
  onSendSelected: () => void;
  onDeleteSelected: () => void;
  loading: boolean;
}

export const InvoiceToolbar: React.FC<InvoiceToolbarProps> = ({
  selectedYear,
  onYearChange,
  selectedStatus,
  onStatusChange,
  selectedInvoices,
  onGenerateAll,
  onCreateCustom,
  onApproveSelected,
  onSendSelected,
  onDeleteSelected,
  loading,
}) => {
  const currentYear = new Date().getFullYear();
  const yearOptions: { label: string; value: number | null }[] = [
    { label: 'All Years', value: null },
  ];
  for (let year = currentYear + 1; year >= currentYear - 5; year--) {
    yearOptions.push({ label: year.toString(), value: year });
  }

  const statusOptions = [
    { label: 'All Status', value: 'ALL' },
    { label: 'Draft', value: InvoiceStatus.DRAFT },
    { label: 'Approved', value: InvoiceStatus.APPROVED },
    { label: 'Sent', value: InvoiceStatus.SENT },
    { label: 'Paid', value: InvoiceStatus.PAID },
    { label: 'Overdue', value: InvoiceStatus.OVERDUE },
    { label: 'Cancelled', value: InvoiceStatus.CANCELLED },
  ];

  const selectedDraftCount = selectedInvoices.filter((inv) => inv.status === InvoiceStatus.DRAFT).length;
  const selectedApprovedCount = selectedInvoices.filter((inv) => inv.status === InvoiceStatus.APPROVED).length;

  return (
    <div data-component="invoice-toolbar" className="flex flex-wrap gap-1 items-center mb-2">
      <Dropdown
        value={selectedYear}
        options={yearOptions}
        onChange={(e) => onYearChange(e.value)}
        placeholder="All Years"
        className="p-inputtext-sm"
        style={{ width: '6rem' }}
      />

      <Dropdown
        value={selectedStatus}
        options={statusOptions}
        onChange={(e) => onStatusChange(e.value)}
        placeholder="Status"
        className="p-inputtext-sm"
        style={{ width: '6.5rem' }}
      />

      <Button
        icon="pi pi-plus"
        onClick={onCreateCustom}
        disabled={loading}
        className="p-button-secondary p-button-sm"
        tooltip="Custom Invoice"
        tooltipOptions={{ position: 'bottom' }}
      />
      <Button
        icon="pi pi-file-pdf"
        onClick={onGenerateAll}
        disabled={loading}
        className="p-button-success p-button-sm"
        tooltip="Generate All"
        tooltipOptions={{ position: 'bottom' }}
      />

      {selectedInvoices.length > 0 && (
        <>
          <Button
            icon="pi pi-check"
            onClick={onApproveSelected}
            disabled={loading || selectedDraftCount === 0}
            className="p-button-info p-button-sm"
            tooltip={`Approve${selectedDraftCount > 0 ? ` (${selectedDraftCount})` : ''}`}
            tooltipOptions={{ position: 'bottom' }}
          />
          <Button
            icon="pi pi-send"
            onClick={onSendSelected}
            disabled={loading || selectedApprovedCount === 0}
            className="p-button-primary p-button-sm"
            tooltip={`Send${selectedApprovedCount > 0 ? ` (${selectedApprovedCount})` : ''}`}
            tooltipOptions={{ position: 'bottom' }}
          />
          <Button
            icon="pi pi-trash"
            onClick={onDeleteSelected}
            disabled={loading || selectedDraftCount === 0}
            className="p-button-danger p-button-sm"
            tooltip="Delete Selected"
            tooltipOptions={{ position: 'bottom' }}
          />
          <span className="text-xs text-gray-500">
            {selectedInvoices.length} sel.
          </span>
        </>
      )}
    </div>
  );
};
